import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LANGUAGE_NAMES,
  UI_LANGUAGES,
  type Settings as SettingsType,
  type SettingsResponse,
  type UiLanguage,
} from '@b4b/shared';
import {
  useSettings,
  useUpdateSettings,
  useMutes,
  useAddMute,
  useDeleteMute,
  useLogout,
  useWeeklyStats,
} from '../api/queries.js';
import { api, ApiError } from '../api/client.js';
import { applyTheme } from '../theme.js';
import NotificationSettings from '../components/NotificationSettings.js';
import PremiumLock from '../components/PremiumLock.js';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <h2 className="mb-3 font-semibold">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`min-h-[36px] flex-1 rounded-lg px-3 text-sm ${
            value === option.value
              ? 'bg-white font-medium shadow-sm dark:bg-neutral-700'
              : 'text-neutral-500'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Label + control. `stacked` puts the control on its own full-width line
 * (needed for wide controls like the 4-option layout switch on phones). */
function Row({ label, children, stacked = false }: { label: string; children: ReactNode; stacked?: boolean }) {
  if (stacked) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-sm">{label}</span>
        <div className="min-w-0">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm">{label}</span>
      <div className="min-w-0 flex-1 max-w-[60%]">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`ml-auto block h-7 w-12 rounded-full p-1 transition ${
        checked ? 'bg-accent' : 'bg-neutral-300 dark:bg-neutral-700'
      }`}
    >
      <span
        className={`block h-5 w-5 rounded-full bg-white transition ${checked ? 'translate-x-5' : ''}`}
      />
    </button>
  );
}

export default function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const settings = useSettings();
  const update = useUpdateSettings();
  const logout = useLogout();

  const patch = (p: Partial<SettingsType>) => update.mutate(p);
  const s = settings.data;

  if (!s) {
    return <p className="py-12 text-center text-neutral-500">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-4 px-4 pb-4 pt-20">
      <h1 className="text-2xl font-bold">{t('settings.title')}</h1>

      <WeeklyCard />

      <PremiumSection settings={s} onPatch={patch} />

      <Section title={t('settings.appearance')}>
        <Row label={t('settings.theme')}>
          <SegmentedControl
            value={s.theme}
            options={[
              { value: 'auto', label: t('settings.themeAuto') },
              { value: 'light', label: t('settings.themeLight') },
              { value: 'dark', label: t('settings.themeDark') },
            ]}
            onChange={(theme) => {
              applyTheme(theme);
              patch({ theme });
            }}
          />
        </Row>
        <Row label={t('settings.fontSize')}>
          <SegmentedControl
            value={s.fontSize}
            options={[
              { value: 'sm', label: t('settings.fontSizeSm') },
              { value: 'md', label: t('settings.fontSizeMd') },
              { value: 'lg', label: t('settings.fontSizeLg') },
            ]}
            onChange={(fontSize) => {
              document.body.className = document.body.className.replace(/font-size-\w+/g, '');
              document.body.classList.add(`font-size-${fontSize}`);
              patch({ fontSize });
            }}
          />
        </Row>
        <Row label={t('settings.feedLayout')} stacked>
          <SegmentedControl
            value={s.feedLayout}
            options={[
              { value: 'reels', label: t('feed.layout.reels') },
              { value: 'list', label: t('feed.layout.list') },
              { value: 'grid', label: t('feed.layout.grid') },
              { value: 'topics', label: t('feed.layout.topics') },
            ]}
            onChange={(feedLayout) => patch({ feedLayout })}
          />
        </Row>
        <Row label={t('settings.language')}>
          <select
            value={s.language}
            onChange={(e) => {
              const language = e.target.value as UiLanguage;
              void i18n.changeLanguage(language);
              patch({ language });
            }}
            className="w-full max-w-full min-h-[44px] rounded-xl border border-neutral-300 bg-white px-3 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            aria-label={t('settings.language')}
          >
            {UI_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {LANGUAGE_NAMES[code]}
              </option>
            ))}
          </select>
        </Row>
      </Section>

      <Section title={t('settings.behavior')}>
        <Row label={t('settings.markReadOnOpen')}>
          <Toggle
            checked={s.markReadOnOpen}
            onChange={(markReadOnOpen) => patch({ markReadOnOpen })}
          />
        </Row>
      </Section>

      <NotificationSettings settings={s} onPatch={patch} />

      <MutesSection />

      <AccountSection onLogout={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })} />
    </div>
  );
}

/** Premium status + the translation toggle it gates. No upsell, just a lock when inactive. */
function PremiumSection({
  settings,
  onPatch,
}: {
  settings: SettingsResponse;
  onPatch: (p: Partial<SettingsType>) => void;
}) {
  const { t, i18n } = useTranslation();
  const premium = settings.premium;
  const until = premium.until
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(premium.until))
    : null;
  return (
    <Section title={t('premium.title')}>
      <p className="text-sm">
        {premium.active && until ? t('premium.activeUntil', { date: until }) : t('premium.inactive')}
      </p>
      {/* locked state stacks the lock label under the row label so it never overflows a phone width */}
      <Row label={t('premium.translateContent')} stacked={!premium.active}>
        {premium.active ? (
          <Toggle
            checked={settings.translateContent}
            onChange={(translateContent) => onPatch({ translateContent })}
          />
        ) : (
          <PremiumLock />
        )}
      </Row>
      <p className="text-xs text-neutral-500">{t('premium.translateHint')}</p>
      <p className="text-xs text-neutral-500">{t('premium.inviteOnly')}</p>
    </Section>
  );
}

function WeeklyCard() {
  const { t } = useTranslation();
  const stats = useWeeklyStats();
  if (!stats.data) return null;
  return (
    <div className="rounded-2xl bg-accent/10 p-4 text-sm">
      <p className="font-semibold text-accent">{t('settings.weekly')}</p>
      <p className="mt-1">
        {t('settings.weeklyRead', { count: stats.data.readCount })} ·{' '}
        {t('settings.weeklySaved', { count: stats.data.savedCount })}
      </p>
      <p className="text-neutral-500">
        {t('settings.weeklyTimeSaved', { min: stats.data.minutesSavedEstimate })}
      </p>
    </div>
  );
}

function MutesSection() {
  const { t } = useTranslation();
  const mutes = useMutes();
  const addMute = useAddMute();
  const deleteMute = useDeleteMute();
  const [keyword, setKeyword] = useState('');

  return (
    <Section title={t('settings.mutes')}>
      <p className="text-xs text-neutral-500">{t('settings.mutesHint')}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (keyword.trim().length >= 2) {
            addMute.mutate({ keyword: keyword.trim() }, { onSuccess: () => setKeyword('') });
          }
        }}
        className="flex gap-2"
      >
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={t('settings.muteAdd')}
          className="flex-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
        />
        <button
          type="submit"
          className="rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong"
        >
          +
        </button>
      </form>
      <div className="flex flex-wrap gap-2">
        {(mutes.data ?? []).map((mute) => (
          <span
            key={mute.id}
            className="flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-sm dark:bg-neutral-800"
          >
            {mute.keyword}
            <button
              onClick={() => deleteMute.mutate(mute.id)}
              aria-label={t('settings.delete')}
              className="text-neutral-400 hover:text-red-500"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    </Section>
  );
}

function AccountSection({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setPasswordMsg(t('settings.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPasswordMsg(
        err instanceof ApiError && err.code === 'invalid_credentials'
          ? t('auth.errors.invalid_credentials')
          : t('auth.errors.generic'),
      );
    }
  };

  const deleteAccount = async () => {
    setDeleteError(null);
    try {
      await api.delete('/api/account', { password: deletePassword });
      window.location.href = '/login';
    } catch {
      setDeleteError(t('auth.errors.invalid_credentials'));
    }
  };

  return (
    <Section title={t('settings.account')}>
      <form onSubmit={(e) => void changePassword(e)} className="space-y-2">
        <p className="text-sm font-medium">{t('settings.changePassword')}</p>
        <input
          type="password"
          autoComplete="current-password"
          placeholder={t('settings.currentPassword')}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
        />
        <div className="flex gap-2">
          <input
            type="password"
            autoComplete="new-password"
            placeholder={t('settings.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="flex-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
          />
          <button
            type="submit"
            disabled={!currentPassword || newPassword.length < 8}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {t('settings.save')}
          </button>
        </div>
        {passwordMsg && <p className="text-sm text-neutral-500">{passwordMsg}</p>}
      </form>

      <div className="flex flex-wrap gap-3 border-t border-neutral-200 pt-3 dark:border-neutral-800">
        <a href="/api/account/export" download className="text-sm text-accent hover:underline">
          {t('settings.exportData')}
        </a>
        <button onClick={onLogout} className="text-sm text-accent hover:underline">
          {t('nav.logout')}
        </button>
        <button
          onClick={() => setDeleteOpen((v) => !v)}
          className="text-sm text-red-500 hover:underline"
        >
          {t('settings.deleteAccount')}
        </button>
      </div>

      {deleteOpen && (
        <div className="space-y-2 rounded-xl border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
          <p className="text-sm">{t('settings.deleteAccountConfirm')}</p>
          <div className="flex gap-2">
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder={t('auth.password')}
              className="flex-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            />
            <button
              onClick={() => void deleteAccount()}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              {t('settings.delete')}
            </button>
            <button
              onClick={() => setDeleteOpen(false)}
              className="rounded-xl border border-neutral-300 px-4 py-2 text-sm dark:border-neutral-700"
            >
              {t('settings.cancel')}
            </button>
          </div>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
        </div>
      )}
    </Section>
  );
}
